import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import { Elements } from '@stripe/react-stripe-js'
import { StripeElementsOptions } from '@stripe/stripe-js'

import { useOSDetection } from '@/hooks/detectOS/useOSDetection'

import { AppCountryTier } from '@/enums/appCountryTier'
import { MobileOperationSystem } from '@/enums/mobileOperationSystem'
import { PageName } from '@/enums/pageName'
import {
  CheckoutFlowVersion,
  PaymentMethod,
  PaymentMethod as PaymentMethodEnum,
  PaymentPlatform,
} from '@/enums/payments'
import { getVersionWithHyphen, PaymentScreenVersion } from '@/enums/paymentScreenVersion'
import { StripeWalletButtonTheme } from '@/enums/stripe'
import { getDefaultPayPalOptions } from '@/utils/paypalUtils'
import { getDefaultStripeOptions } from '@/utils/stripeUtils'
import { getPath } from '@/utils/urlUtils'

import { PaymentAccordion } from '@/components/accordion'
import { PageTitle } from '@/components/pageTitle'
import { PaymentMethodIcons } from '@/components/paymentMethodIcons'
import { WalletTypeDetector } from '@/components/walletTypeDetector'
import { ApplePay2 } from '@/shared/icons/ApplePay2'
import { GooglePay2 } from '@/shared/icons/GooglePay2'
import { PayPalDark } from '@/shared/icons/PayPalDark'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'

import { paypalButtonMap, stripeCheckoutFormMap, walletPaymentButtonMap } from './componentMappings'
import { useProcessContactState } from './hooks/contactState/useProcessContactState'
import useTrackPageAndPaymentScreen from './hooks/trackWebEvents/useTrackWebEvents'
import { TPaymentScreenPageProps } from './types'

const paypalOptions = getDefaultPayPalOptions()

const pageName = PageName.paymentScreen

function getAccordionOrder(
  currentMobileOS: MobileOperationSystem,
  tier: AppCountryTier,
  showStripe: boolean,
  walletType: PaymentMethod | undefined
) {
  if (!showStripe) {
    console.log('stripe is disabled')
    return [PaymentMethod.PAYPAL]
  }
  const order: string[] = [PaymentMethod.CARD, PaymentMethod.PAYPAL]

  let walletIndex = 0
  let walletPaymentMethod

  if (currentMobileOS === MobileOperationSystem.IOS) {
    walletPaymentMethod = PaymentMethod.APPLE_PAY
    if (tier === AppCountryTier.T1 || tier === AppCountryTier.T2) {
      // [APPLE_PAY, CARD, PAYPAL]
      walletIndex = 0
    } else if (tier === AppCountryTier.T3 || tier === AppCountryTier.T4) {
      // [CARD, APPLE_PAY, PAYPAL]
      walletIndex = 1
    }
  } else if (currentMobileOS === MobileOperationSystem.ANDROID) {
    // [CARD, PAYPAL, GOOGLE_PAY]
    walletPaymentMethod = PaymentMethod.GOOGLE_PAY
    walletIndex = 2
  }

  if (walletType && walletPaymentMethod) {
    // add a wallet
    order.splice(walletIndex, 0, walletPaymentMethod)
  }

  console.log('currentMobileOS', currentMobileOS)
  console.log('order', order)

  return order
}

export const PaymentScreenModalV3_4: React.FunctionComponent<TPaymentScreenPageProps> = ({
  getItem,
  setItem,
  pageConfig,
  selectPlanPage,
  config,
  stripePromise,
  stripeFormAppearance,
  paypalButtonConfigStyle,
  getStripeWalletButtonStyles,
  successRedirectTo = PageName.paymentSuccess,
  contactStateRoutesMap,
}: TPaymentScreenPageProps) => {
  const { user } = useAuth()
  const { checkoutFlowVersion } = useSession()
  const navigate = useNavigate()
  const mobileOS = useOSDetection()
  const tier = getItem('appCountryTier') as AppCountryTier
  const {
    discount,
    pageTitle,
    customTitle,
    stripeFormConfig,
    paypalCustomTagline,
    receiptData,
    loadingTitle,
  } = pageConfig

  const [walletType, setWalletType] = useState<PaymentMethodEnum | undefined>(undefined)

  const showPayPal = useMemo<boolean>(
    () => config.PAYMENT_METHODS.includes(PaymentPlatform.PAYPAL),
    [config]
  )
  const showStripe = useMemo<boolean>(
    () => config.PAYMENT_METHODS.includes(PaymentPlatform.STRIPE),
    [config]
  )

  const selectedPlanId = getItem(PageName.selectPlan)
  const planData = useMemo(
    () => selectPlanPage.plansDetails.find((plan) => plan.planId === selectedPlanId),
    [selectPlanPage.plansDetails, selectedPlanId]
  )

  const [accordionOrder, setAccordionOrder] = useState<string[]>([])

  const [openedAccordion, setOpenedAccordion] = useState<string | null>(() => {
    const accordionOrder = getAccordionOrder(mobileOS, tier, showStripe, walletType)
    return accordionOrder.length > 0 ? accordionOrder[0] : null
  })

  useEffect(() => {
    if (accordionOrder.length > 0) {
      setOpenedAccordion(accordionOrder[0])
    }
  }, [accordionOrder])

  const handleWalletTypeChange = useCallback(
    (type: PaymentMethodEnum | undefined) => {
      setWalletType(type)
      const defaultAccordionOrder = getAccordionOrder(mobileOS, tier, showStripe, type)
      setAccordionOrder(defaultAccordionOrder)
    },
    [setWalletType, mobileOS, tier, showStripe]
  )

  useEffect(() => {
    // should be run once
    if (!planData) {
      return navigate(getPath(PageName.selectPlan))
    }
  }, [navigate, planData])

  useEffect(() => {}, [walletType])

  const stripeOptions = useMemo<StripeElementsOptions | undefined>(
    () => getDefaultStripeOptions(planData, stripeFormAppearance),
    [planData, stripeFormAppearance]
  )

  const paymentScreenVersion = getVersionWithHyphen(
    config.PAYMENT_SCREEN_VERSION as PaymentScreenVersion
  )
  const className = `page page-${pageName}-${paymentScreenVersion}`

  useTrackPageAndPaymentScreen(config, getItem, setItem, pageTitle, planData, pageName)

  const toggleAccordion = (id: string) => {
    setOpenedAccordion((prevState) => (prevState !== id ? id : prevState))
  }

  const contactStateEl = useProcessContactState(
    config.PAGE_SET_ID,
    getItem,
    setItem,
    { className, stateRoutes: contactStateRoutesMap },
    checkoutFlowVersion
  )
  if (contactStateEl) {
    return contactStateEl
  }

  if (!user && checkoutFlowVersion !== CheckoutFlowVersion.V2) {
    // TODO: probably there are better typescript hints to avoid this check here
    return <Navigate to={getPath(PageName.registration)} />
  }

  if (!planData || !selectedPlanId) {
    return <Navigate to={getPath(PageName.selectPlan)} />
  }

  if (!stripeOptions) {
    return null
  }

  const WalletPaymentFC = walletPaymentButtonMap[checkoutFlowVersion]
  const PayPalButtonFC = paypalButtonMap[checkoutFlowVersion]
  const StripeCheckoutFormFC = stripeCheckoutFormMap[checkoutFlowVersion]

  return (
    <div className={className}>
      <PayPalScriptProvider options={{ ...paypalOptions }}>
        <Elements stripe={stripePromise} options={stripeOptions}>
          <WalletTypeDetector
            getItem={getItem}
            setItem={setItem}
            onWalletTypeChange={handleWalletTypeChange}
            loadingTitle={loadingTitle}
          >
            <PageTitle title={customTitle || discount} />
            <div className="floating-container">
              {accordionOrder.map((accordionId) => {
                if (
                  (accordionId === PaymentMethod.APPLE_PAY ||
                    accordionId === PaymentMethod.GOOGLE_PAY) &&
                  receiptData &&
                  showStripe
                ) {
                  return (
                    <PaymentAccordion
                      key={accordionId}
                      receiptData={receiptData[selectedPlanId]}
                      icon={
                        <div className="wallet-icons">
                          {walletType === PaymentMethodEnum.APPLE_PAY && <ApplePay2 />}
                          {walletType === PaymentMethodEnum.GOOGLE_PAY && <GooglePay2 />}
                        </div>
                      }
                      body={
                        <WalletPaymentFC
                          planData={planData}
                          getItem={getItem}
                          setItem={setItem}
                          pageName={pageName}
                          config={config}
                          stripeButtonTheme={StripeWalletButtonTheme.dark}
                          getStripeWalletButtonStyles={getStripeWalletButtonStyles}
                          successRedirectTo={successRedirectTo}
                          contactStateRoutesMap={contactStateRoutesMap}
                        />
                      }
                      title={`${
                        walletType === PaymentMethodEnum.APPLE_PAY
                          ? 'Apple Pay'
                          : walletType === PaymentMethodEnum.GOOGLE_PAY
                            ? 'Google Pay'
                            : ''
                      }`}
                      id={accordionId}
                      isOpen={openedAccordion === accordionId}
                      onToggle={toggleAccordion}
                    />
                  )
                }

                if (accordionId === PaymentMethod.PAYPAL && receiptData && showPayPal) {
                  return (
                    <PaymentAccordion
                      key={accordionId}
                      receiptData={receiptData[selectedPlanId]}
                      icon={<PayPalDark />}
                      body={
                        <PayPalButtonFC
                          pageName={pageName}
                          paypalButtonConfigStyle={paypalButtonConfigStyle}
                          productData={planData}
                          getItem={getItem}
                          setItem={setItem}
                          config={config}
                          customTagline={paypalCustomTagline}
                          successRedirectTo={successRedirectTo}
                          contactStateRoutesMap={contactStateRoutesMap}
                        />
                      }
                      title={`PayPal`}
                      id={accordionId}
                      isOpen={openedAccordion === accordionId}
                      onToggle={toggleAccordion}
                    />
                  )
                }

                if (accordionId === PaymentMethod.CARD && receiptData && showStripe) {
                  return (
                    <PaymentAccordion
                      key={accordionId}
                      receiptData={receiptData[selectedPlanId]}
                      icon={
                        <PaymentMethodIcons
                          order={stripeFormConfig?.paymentMethodSwitcherIcons?.order}
                        />
                      }
                      body={
                        <StripeCheckoutFormFC
                          pageName={pageName}
                          getItem={getItem}
                          setItem={setItem}
                          planDetails={planData}
                          config={config}
                          formConfig={stripeFormConfig}
                          successRedirectTo={successRedirectTo}
                          contactStateRoutesMap={contactStateRoutesMap}
                        />
                      }
                      title={'Credit card'}
                      id={accordionId}
                      isOpen={openedAccordion === accordionId}
                      onToggle={toggleAccordion}
                    />
                  )
                }
                return null
              })}
            </div>
          </WalletTypeDetector>
        </Elements>
      </PayPalScriptProvider>
    </div>
  )
}
